import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, /* List, ListItem */ } from '@mui/material';
import Typography from '../../typography/typography';
import './barLineChart.scss';

interface DataPoint {
    [key: string]: string | number;
}

interface ChartField {
    field: string;
    color: string;
    label: string;
}

export interface IBarLineChartProps {
    data: DataPoint[];
    width?: number;
    height?: number;
    margin?: { top: number; right: number; bottom: number; left: number };
    bars: ChartField[];
    lines: ChartField[];
    line?: boolean;
    showGrid?: boolean;
    barColors?: any[];
    lineColors?: any[];
}

const BarLineChart: React.FC<IBarLineChartProps> = ({
    data,
    height = 400,
    margin = { top: 20, right: 30, bottom: 30, left: 50 },
    bars,
    lines,
    line = true,
    showGrid = true,
    barColors,
    lineColors
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [tooltip, setTooltip] = useState<{ x: number; y: number; content: DataPoint } | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const formatNumber = (num: number) => d3.format('.2s')(num).replace('k', 'K');

    const needsHorizontalScroll = data.length > bars.length;
    const minBarWidth = 60
    const calculatedChartWidth = needsHorizontalScroll 
        ? Math.max(data.length * minBarWidth, containerWidth)
        : containerWidth;

    useEffect(() => {
        const wrapper = wrapperRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const newWidth = entries[0].contentRect.width;
                setContainerWidth(newWidth);
            }
        });

        if (wrapper) {
            resizeObserver.observe(wrapper);
        }

        return () => {
            if (wrapper) {
                resizeObserver.unobserve(wrapper);
            }
        };
    }, [])
    
    useEffect(() => {
        if (containerWidth === 0) return;
        const yMax = d3.max(data, d => {
            return Math.max(
                ...bars.map(bar => d[bar.field] as number),
                ...lines.map(line => d[line.field] as number)
            );
        }) ?? 0;
        const yMin = d3.min(data, d => {
            return Math.min(
                ...bars.map(bar => d[bar.field] as number),
                ...lines.map(line => d[line.field] as number)
            );
        }) ?? 0;

        const svg = d3.select(svgRef.current);
        const innerWidth = calculatedChartWidth - margin.left - margin.right;
        const innerHeight = yMax || yMin ? height - margin.top - margin.bottom : 0;

        const x = d3.scaleBand()
            .domain(data.map(d => d.month as string))
            .range([0, innerWidth])
            .padding(0.3);

        const y = d3.scaleLinear()
            .domain([yMin, yMax])
            .nice()
            .range([innerHeight, 0]);

        svg.selectAll('*').remove();

        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Y axis
        const yAxis = g.append('g')
            .call(d3.axisLeft(y).tickFormat(formatNumber))
            .call(g => g.select('.domain').remove());
        yAxis.select(".domain").attr("stroke", "none")
        
        // X axis
        const xAxis = g.append('g')
            .attr('transform', `translate(0,${innerHeight})`)
            .call(d3.axisBottom(x))
            .selectAll('.tick text')
            .attr('text-anchor', 'middle');
        xAxis.select(".domain").attr("stroke", "none")
        
        // Render grid lines if applicable
        if (showGrid) {
            g.selectAll('.grid-line')
                .data(y.ticks())
                .enter()
                .append('line')
                .attr('class', 'grid-line')
                .attr('x1', 0)
                .attr('x2', innerWidth)
                .attr('y1', d => y(d))
                .attr('y2', d => y(d))
                .style('stroke', '#ddd')
                .style('stroke-dasharray', '8,8') // Dotted line
                .style('stroke-width', 1);
        }
        
        // Render grid lines if applicable
        if (line) {
            g.selectAll('.grid-line-2')
                .data(y.ticks())
                .enter()
                .append('line')
                .attr('class', 'grid-line-2')
                .attr('x1', 0)
                .attr('x2', innerWidth)
                .attr('y1', d => y(d))
                .attr('y2', d => y(d))
                .style('stroke', '#ddd')
                .style('stroke-dasharray', '8,8') // Dotted line
                .style('stroke-width', 1)
        }

        // Draw bars for bar chart
        bars.forEach((bar, i) => {
            g.selectAll(`.bar-${bar.field}`)
                .data(data)
                .enter()
                .append('rect')
                .attr('class', `bar bar-${bar.field}`)
                .attr('x', d => x(d.month as string) ?? 0)
                .attr('y', d => d[bar.field] >= 0 ? y(d[bar.field]) : y(0))  // Positive bars above y(0), negative bars below
                .attr("rx", 5)
                .attr("ry", 5)
                .attr('width', x.bandwidth() / bars.length)
                .attr('height', d => {
                    return Math.abs(y(d[bar.field]) - y(0));  // Absolute height from y(0)
                })
                .attr('fill', barColors[i])
                .attr('transform', `translate(${(i * x.bandwidth()) / bars.length}, 0)`)
                .on('mouseover', function (event, d) {
                    const [xPos, yPos] = d3.pointer(event, svgRef.current);
                    const scrollLeft = scrollContainerRef.current?.scrollLeft || 0;
                    setTooltip({ 
                        x: xPos + margin.left - scrollLeft, 
                        y: yPos + margin.top, 
                        content: d 
                    });
                })
                .on('mouseout', () => setTooltip(null));
        });
        
        // Draw lines for line chart
        lines.forEach((line, index) => {
            const lineGenerator = d3.line<DataPoint>()
                .x(d => (x(d.month as string) ?? 0) + x.bandwidth() / 2) // Calculate x position for each data point
                .y(d => y(d[line.field] as number))
                .curve(d3.curveMonotoneX);

            g.append('path')
                .datum(data)
                .attr('fill', 'none')
                .attr('stroke', lineColors[index])
                .attr('stroke-width', 2)
                .attr('d', lineGenerator)
                .on('mouseover', function (event) {
                    const [xPos] = d3.pointer(event, svgRef.current);
                    const month = x.domain().find((month) => {
                        const monthPosition = x(month as string) ?? 0;
                        return xPos >= monthPosition && xPos <= monthPosition + x.bandwidth();
                    });

                    if (month) {
                        const closestDataPoint = data.find(d => d.month === month);
                        if (closestDataPoint) {
                            const scrollLeft = scrollContainerRef.current?.scrollLeft || 0;
                            setTooltip({
                                x: xPos + margin.left - scrollLeft,
                                y: y(closestDataPoint[line.field]) + margin.top,
                                content: closestDataPoint
                            });
                        }
                    }
                })
                .on('mouseout', () => setTooltip(null));
        });
    }, [data, containerWidth, calculatedChartWidth, line, height, margin, bars, lines, showGrid, barColors, lineColors]);

    return (
        <div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
            <div style={{ position: 'relative' }}>
                <div 
                    ref={scrollContainerRef}
                    style={{ 
                        width: '100%', 
                        overflowX: needsHorizontalScroll ? 'auto' : 'hidden',
                        overflowY: 'hidden'
                    }}
                >
                    <svg 
                        ref={svgRef} 
                        width={calculatedChartWidth} 
                        height={height} 
                        viewBox={`0 0 ${calculatedChartWidth} ${height}`}
                        style={{ display: 'block' }}
                    />
                </div>
                {tooltip && (
                    <div
                        style={{
                            position: 'absolute',
                            left: tooltip.x,
                            top: tooltip.y,
                            pointerEvents: 'none',
                            backgroundColor: 'white',
                            zIndex: '99999',
                            border: 'none',
                            padding: '5px',
                            borderRadius: '3px',
                            transform: 'translate(-50%, -100%)'
                        }}
                    >
                        <Box>
                            <div style={{ background: '#F3F2F2', padding: '10px' }}>
                                <Typography type='s4' style={{ whiteSpace: 'noWrap', width: '200px', color: '#232529' }}>{tooltip.content?.month}</Typography>
                            </div>
                            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                                {bars.map((bar, index) => (
                                    <Box key={`bar-${index}`} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                        <div className="circle" style={{ backgroundColor: barColors[index] }} />
                                        <Typography type='s5' color='theme.secondary.900'>
                                            {bar.label}: <span style={{ fontWeight: 600 }}>$ {tooltip.content[bar.field]}</span>
                                        </Typography>
                                    </Box>
                                ))}
                                {lines.map((line, index) => (
                                    <Box key={`line-${index}`} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                        <div className="circle" style={{ backgroundColor: lineColors[index] }} />
                                        <Typography type='s5' color='theme.secondary.900'>
                                            {line.label}: <span style={{ fontWeight: 600 }}>$ {tooltip.content[line.field]}</span>
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                      
                    </div>
                )
                }
            </div>
        </div>
    );
};

export default BarLineChart;